export type HeaderLevel = 1 | 2 | 3 | 4 | 5 | 6;
// Sorted in order from smallest to largest heading size
export const headerNumericalLevels: HeaderLevel[] = [6, 5, 4, 3, 2, 1];

/**
 * Increases or decreases all occurrences of a heading by a given `adjustment` factor.
 *
 * @param doc The HTML document
 * @param level The heading level to target
 * @param adjustment How much to increase (or decrease, if negative) the heading level by
 */
export const replaceHeaders = (
	doc: Document,
	level: HeaderLevel,
	adjustment: number,
) => {
	doc.querySelectorAll(`h${level}`).forEach((element) => {
		if (!element.parentNode) {
			return;
		}
		// Can't exceed h1 or h6
		const newLevel = Math.max(
			Math.min(level + adjustment, headerNumericalLevels[0]),
			headerNumericalLevels[headerNumericalLevels.length - 1],
		);
		const newElement = doc.createElement(`h${newLevel}`);
		newElement.innerHTML = element.innerHTML;
		for (let j = 0; j < element.attributes.length; j++) {
			const attrib = element.attributes[j];
			newElement.setAttribute(attrib.name, attrib.value);
		}
		element.parentNode.replaceChild(newElement, element);
		// Alternatively: newElement.outerHTML = element.outerHTML;
		// The alternative removes event listeneres, whereas `replaceChild` does not.
	});
};

/**
 * Adjusts all headers in the given HTML content to start from a given level.
 *
 * Example: If the content is <h1>foo</h1><h2>bar</h2>, but we want headings to start with h3, then
 * the output will be <h3>foo</h3><h4>bar</h4>.
 *
 * @param content The HTML content
 * @param startingHeaderLevel The level where headings should start from
 */
export const adjustHeadersToStartingLevel = (
	content: string,
	startingHeaderLevel: HeaderLevel,
) => {
	// Determine the largest heading in the content
	const parser = new DOMParser();
	const doc = parser.parseFromString(content, 'text/html');
	// Start from h6 and go up to h1 trying to see what exists
	let largestHeader = headerNumericalLevels[0];
	// Go from
	for (const i of headerNumericalLevels) {
		if (doc.querySelector(`h${i}`)) {
			largestHeader = i;
		}
	}

	// Adjust heading levels so they start at `startingHeadingLevel`
	// If there are some bigger headings than `startingHeadingLevel`, we will decrease the heading size by adding
	// a positive adjustment. If the headings are all smaller than the `startingHeadingLevel`, then we increase
	// the heading size by subtracting an adjustment.
	const adjustment = startingHeaderLevel - largestHeader;
	// If adjustment is positive, we want to work from smallest heading to largest heading so that we aren't working
	// items we update. If the adjustment is negative, we want to work the opposite direction.
	// Need to work from smallest heading to largest heading so that we aren't working on items we update
	if (adjustment > 0) {
		// Example: We want to start at h3 and the largest is h1, so we will change these in order
		// h6 -> h6, h5 -> h6, h4 -> h6, h3 -> h5, h2 -> h4, h1 -> h3
		for (let i = headerNumericalLevels[0]; i >= largestHeader; i--) {
			replaceHeaders(doc, i, adjustment);
		}
	} else if (adjustment < 0) {
		// Example: We want to start at h3 and the largest is h4, so we will change these in order
		// h4 -> h3, h5 -> h4, h6 -> h5
		for (let i = largestHeader; i <= headerNumericalLevels[0]; i++) {
			replaceHeaders(doc, i, adjustment);
		}
	}
	// If adjustment is 0, nothing to do...
	return doc.body.innerHTML;
};
